import Cookies from "js-cookie";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";
const auth = () => {
    const t = Cookies.get("token");
    return t ? { Authorization: `Bearer ${t}` } : {};
};

export async function fetchPlanState() {
    const res = await fetch(`${API_URL}/api/plan-state`, { headers: auth() });
    if (!res.ok) throw new Error("Failed to load plan state");
    return res.json();
}

export async function fetchPlans() {
    const res = await fetch(`${API_URL}/api/plans`, { headers: auth() });
    if (!res.ok) throw new Error("Failed to load plans");
    return res.json(); // { plans, featureMeta }
}

export async function previewPlanChange({ targetPlanSlug, billingCycle, currency }) {
    const res = await fetch(`${API_URL}/api/plan/preview`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ targetPlanSlug, billingCycle, currency }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Preview failed");
    return json;
}

export async function changePlan({ targetPlanSlug, billingCycle, currency, promoCode }) {
    const res = await fetch(`${API_URL}/api/plan/change`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ targetPlanSlug, billingCycle, currency, promoCode }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Plan change failed");
    return json; // { subscriptionId, shortUrl, keyId, preview, scheduledAt? }
}

// Partner promo code — returns { code, partnerName, percentOff, planSlug, billingCycle, currency, fullAmount, discountedAmount }.
export async function validatePromo({ code, targetPlanSlug, billingCycle }) {
    const res = await fetch(`${API_URL}/api/promo/validate`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ code, targetPlanSlug, billingCycle }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Invalid code");
    return json;
}

// Partner landing pages stash a code here so it survives signup/login.
export const PROMO_STORAGE_KEY = "cfai_promo_code";
export function getStoredPromo() {
    try { return localStorage.getItem(PROMO_STORAGE_KEY) || null; } catch (e) { return null; }
}
export function storePromo(code) {
    try { code ? localStorage.setItem(PROMO_STORAGE_KEY, code) : localStorage.removeItem(PROMO_STORAGE_KEY); } catch (e) { /* storage blocked */ }
}

export async function fetchByok() {
    const res = await fetch(`${API_URL}/api/byok`, { headers: auth() });
    if (!res.ok) throw new Error("Failed to load key status");
    return res.json(); // { active, last4 }
}

export async function saveByok(apiKey) {
    const res = await fetch(`${API_URL}/api/byok`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ apiKey }),
    });
    const json = await res.json();
    if (!res.ok) {
        const err = new Error(json.error || "Failed to save key");
        err.status = res.status;
        err.requiresUpgrade = res.status === 403;
        err.upgradeUrl = json.upgrade_url;
        throw err;
    }
    return json;
}

export async function removeByok() {
    const res = await fetch(`${API_URL}/api/byok`, { method: "DELETE", headers: auth() });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Failed to remove key");
    return json;
}

export async function fetchUsage() {
    const res = await fetch(`${API_URL}/api/usage/current`, { headers: auth() });
    if (!res.ok) throw new Error("Failed to load usage");
    return res.json(); // { meters: { responses, ai_generations }, warnings[] }
}

export async function fetchAddonCatalog() {
    const res = await fetch(`${API_URL}/api/addons`, { headers: auth() });
    if (!res.ok) throw new Error("Failed to load add-ons");
    return res.json(); // { addons }
}

export async function fetchActiveAddons() {
    const res = await fetch(`${API_URL}/api/addons/active`, { headers: auth() });
    if (!res.ok) throw new Error("Failed to load active add-ons");
    return res.json(); // { addons }
}

export async function cancelPlan() {
    const res = await fetch(`${API_URL}/api/plan/cancel`, { method: "POST", headers: auth() });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Cancel failed");
    return json;
}

export async function reactivatePlan() {
    const res = await fetch(`${API_URL}/api/plan/reactivate`, { method: "POST", headers: auth() });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Reactivate failed");
    return json;
}

async function createAddonOrder({ sku, qty = 1, currency }) {
    const res = await fetch(`${API_URL}/api/addons/order`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ sku, qty, currency }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Could not start add-on checkout");
    return json; // { orderId, amount, currency, keyId, sku, qty }
}

async function verifyAddonPayment(payload) {
    const res = await fetch(`${API_URL}/api/addons/verify`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Add-on payment verification failed");
    return json;
}

// One-time capacity add-on purchase: order → inline Checkout.js → verify + apply.
export async function purchaseAddon({ sku, qty = 1, currency, name }, { onSuccess, onDismiss, onError } = {}) {
    const order = await createAddonOrder({ sku, qty, currency });
    const ok = await loadRazorpay();
    if (!ok || !window.Razorpay) throw new Error("Could not load checkout");
    const rzp = new window.Razorpay({
        key: order.keyId,
        order_id: order.orderId,
        amount: order.amount,
        currency: order.currency,
        name: "Content Flash AI",
        description: name || "Capacity add-on",
        theme: { color: "#4f46e5" },
        handler: async (r) => {
            try {
                await verifyAddonPayment({
                    razorpay_order_id: r.razorpay_order_id,
                    razorpay_payment_id: r.razorpay_payment_id,
                    razorpay_signature: r.razorpay_signature,
                    sku, qty,
                });
                onSuccess && onSuccess();
            } catch (e) {
                onError && onError(e);
            }
        },
        modal: { ondismiss: () => { onDismiss && onDismiss(); } },
    });
    rzp.open();
}

// Lazy-load Razorpay Checkout.js once.
let razorpayPromise = null;
export function loadRazorpay() {
    if (window.Razorpay) return Promise.resolve(true);
    if (razorpayPromise) return razorpayPromise;
    razorpayPromise = new Promise((resolve) => {
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
    return razorpayPromise;
}

// Opens the SAME Razorpay flow for every trigger. Inline Checkout.js when available,
// otherwise falls back to the hosted short URL. Resolves true on payment success.
export async function openRazorpayCheckout({ subscriptionId, shortUrl, keyId, planName }, { onSuccess, onDismiss } = {}) {
    const ok = await loadRazorpay();
    if (ok && window.Razorpay && subscriptionId && keyId) {
        const rzp = new window.Razorpay({
            key: keyId,
            subscription_id: subscriptionId,
            name: "Content Flash AI",
            description: planName ? `Upgrade to ${planName}` : "Subscription",
            theme: { color: "#4f46e5" },
            handler: () => { onSuccess && onSuccess(); },
            modal: { ondismiss: () => { onDismiss && onDismiss(); } },
        });
        rzp.open();
        return "opened";
    }
    // Fallback: hosted checkout page
    if (shortUrl) { window.location.href = shortUrl; return "redirect"; }
    throw new Error("Could not start checkout");
}
